import { HdBodygraphChartPreset, HdBodygraphConfig } from '../hd-bodygraph.models';
import { createEmptyHdBodygraphConfig } from '../hd-bodygraph-config.utils';
import { HD_BODYGRAPH_GATES } from '../hd-bodygraph-metadata';
import { HdChartGameId, HdChartGameVariantId } from '../hd-chart-controls-options';
import { HdChartSideMode } from './hd-chart-shortcut-bar.component';
import { PlatformChartType } from '@data/_generated-api/platform-api/models/platform-chart-type';

/**
 * Per-tab snapshot van de chart-omgeving (spec
 * `docs/superpowers/specs/2026-06-11-chart-tabs-sidepanel-design.md` §6).
 * Wordt als geheel in localStorage bewaard onder `hdChartTabs`.
 */
export interface HdChartTabGameSettingsV1 {
  gameMode: boolean;
  selectedGameId: HdChartGameId;
  selectedVariantId: HdChartGameVariantId;
  gateRangeSortEnabled: boolean;
  gateRangeStart: number;
  gateRangeEnd: number;
  /** Doel-chart (library-id) van "Teken je chart"; '' = nog niet gekozen. */
  drawChartTargetId: string;
}

export interface HdChartTabSnapshotV1 {
  schemaVersion: 1;
  id: string;
  /** Naam van de geladen preset — tabtitel-bron, onafhankelijk van modified. */
  presetName: string | null;
  presetModified: boolean;
  /** Volledig geladen preset (incl. planetaryActivations) of null in vrije modus. */
  preset: HdBodygraphChartPreset | null;
  /** Library-chart-id zodra deze tab opgeslagen is; null = nog niet opgeslagen. */
  savedChartId?: string | null;
  /** Chart-type voor de save-actie (Standard/JustNow/…). Default Standard. */
  chartType?: PlatformChartType;
  /** Niet-opgeslagen wijzigingen aan een reeds opgeslagen chart (bv. na recalc) → sterretje + Ctrl-S = update. */
  pendingSaveChanges?: boolean;
  /**
   * Gedeelde chart (spec 2026-10-20-chart-share §4.6): token van de deellink waarmee
   * deze tab geopend werd; null/afwezig = eigen of onopgeslagen chart. Het preset zit
   * al in de tab, dus herstarten vraagt geen nieuwe ophaling.
   */
  sharedToken?: string | null;
  /** Voornaam van de deler (voor de banner). */
  sharedByName?: string | null;
  /**
   * Zijde-/kantkeuze van deze tab (design/full/personality) — per tab bewaard
   * sinds de "Toon enkel Just Now"-flow (2026-08-27), zodat een
   * personality-only momenttab andere tabs niet beïnvloedt. Default 'full'.
   */
  chartSideMode?: HdChartSideMode;
  configuration: HdBodygraphConfig;
  game: HdChartTabGameSettingsV1;
}

export interface HdChartTabsStateV1 {
  schemaVersion: 1;
  activeTabId: string;
  tabs: HdChartTabSnapshotV1[];
}

export function generateHdChartTabId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).substring(2, 15);
}

export function createDefaultHdChartTabSnapshot(id: string = generateHdChartTabId()): HdChartTabSnapshotV1 {
  return {
    schemaVersion: 1,
    id,
    presetName: null,
    presetModified: false,
    preset: null,
    savedChartId: null,
    chartType: PlatformChartType.Standard,
    pendingSaveChanges: false,
    chartSideMode: 'full',
    configuration: createEmptyHdBodygraphConfig(),
    game: {
      gameMode: false,
      selectedGameId: 'gate-guessing',
      selectedVariantId: 'numbers',
      gateRangeSortEnabled: false,
      gateRangeStart: HD_BODYGRAPH_GATES[0],
      gateRangeEnd: HD_BODYGRAPH_GATES[HD_BODYGRAPH_GATES.length - 1],
      drawChartTargetId: '',
    },
  };
}

/**
 * Shape-check per tab: de velden die consumers direct dereferencen
 * (`configuration.activations`, `game.*`) moeten aanwezig zijn, anders
 * verwerpen we de hele opgeslagen state en valt de caller terug op defaults.
 */
function isValidTabShape(tab: HdChartTabSnapshotV1 | null | undefined): boolean {
  return tab != null
    && typeof tab.id === 'string'
    && tab.configuration != null
    && Array.isArray(tab.configuration.activations)
    && tab.game != null
    && typeof tab.game.gameMode === 'boolean';
}

/**
 * Een tab zonder preset toont geen chart en mag dus geen library-chart claimen.
 * Vóór 2026-10-06 maakte de spelmodus de tab leeg maar liet `savedChartId`
 * staan; zulke opgeslagen tabs lieten "Mijn chart laden" naar een lege
 * speltab springen. Bij het inlezen rechtzetten.
 */
function releaseSavedChartWithoutPreset(tab: HdChartTabSnapshotV1): HdChartTabSnapshotV1 {
  if (tab.preset != null || tab.savedChartId == null) {
    return tab;
  }
  return { ...tab, savedChartId: null, pendingSaveChanges: false, chartType: PlatformChartType.Standard };
}

/**
 * Parset de opgeslagen tabs-state defensief: corrupte JSON, een afwijkende
 * schemaVersion, een lege tablijst, een onbekende activeTabId of een tab
 * zonder geldige shape leveren `null` op zodat de caller op defaults kan
 * terugvallen.
 */
export function parseHdChartTabsState(raw: string | null): HdChartTabsStateV1 | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as HdChartTabsStateV1;
    if (parsed?.schemaVersion !== 1 || !Array.isArray(parsed.tabs) || parsed.tabs.length === 0) {
      return null;
    }
    if (!parsed.tabs.every(isValidTabShape)) {
      return null;
    }
    if (!parsed.tabs.some(tab => tab.id === parsed.activeTabId)) {
      return null;
    }
    return { ...parsed, tabs: parsed.tabs.map(releaseSavedChartWithoutPreset) };
  } catch {
    console.warn('hdChartTabs: corrupte localStorage-inhoud genegeerd.');
    return null;
  }
}
